import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ChevronLeft, ChevronRight, Layers, Save } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/extranet/StatusBadge";
import BulkEditDialog from "@/components/extranet/BulkEditDialog";
import { http, fmtDate, addDays, todayISO, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";

const DAYS = 14;

const Cell = ({ children, weekend, className = "" }) => (
  <td className={`min-w-[92px] border border-[#CBD5E1] p-0 text-center ${weekend ? "bg-blue-50/40" : ""} ${className}`}>{children}</td>
);

const NumIn = ({ value, onChange, testid, dirty }) => (
  <input type="number" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    className={`h-9 w-full bg-transparent px-1 text-center text-sm outline-none focus:bg-white focus:ring-2 focus:ring-[#006CE4] ${dirty ? "bg-amber-100 font-semibold" : ""}`} />
);

export default function RatesCalendar() {
  const { property } = useOutletContext();
  const t = useT();
  const [start, setStart] = useState(todayISO());
  const [grid, setGrid] = useState(null);
  const [edits, setEdits] = useState({});
  const [bulk, setBulk] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => http.get(`/extranet/properties/${property.id}/calendar`, { params: { start, days: DAYS } }).then((r) => { setGrid(r.data); setEdits({}); });
  useEffect(() => { load(); }, [start, property.id]); // eslint-disable-line

  const k = (rid, d) => `${rid}|${d}`;
  const val = (rid, day, f) => edits[k(rid, day.date)]?.[f] ?? day[f];
  const setVal = (rid, d, f, v) => setEdits((e) => ({ ...e, [k(rid, d)]: { ...e[k(rid, d)], [f]: v } }));
  const count = Object.keys(edits).length;

  const save = async () => {
    setSaving(true);
    const updates = Object.entries(edits).map(([key, v]) => {
      const [room_type_id, date] = key.split("|");
      return { room_type_id, date, ...Object.fromEntries(Object.entries(v).filter(([, x]) => x !== "")) };
    });
    try {
      await http.put(`/extranet/properties/${property.id}/calendar`, { updates });
      toast.success(`${updates.length} sel disimpan & dikirim ke channel manager`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const dates = useMemo(() => grid?.dates || [], [grid]);
  const weekend = useMemo(() => Object.fromEntries(dates.map((d) => [d, [0, 6].includes(new Date(d + "T00:00").getDay())])), [dates]);

  return (
    <div className="fade-up flex flex-col gap-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Tarif & Ketersediaan")}</h1>
          <p className="text-sm text-slate-500">{t("Klik sel untuk ubah harga (IDR) atau jumlah kamar dijual. Perubahan otomatis tersinkron ke channel.")} <b>{t("Harga yang diisi adalah harga final tamu (tanpa pajak & layanan tambahan).")}</b></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setStart(addDays(start, -DAYS))} className="rounded-md border border-slate-300 bg-white p-2 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="cal-today" onClick={() => setStart(todayISO())} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold hover:bg-slate-50">{t("Hari ini")}</button>
          <button data-testid="cal-next" onClick={() => setStart(addDays(start, DAYS))} className="rounded-md border border-slate-300 bg-white p-2 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          <button data-testid="cal-bulk-button" onClick={() => setBulk(true)} className="flex items-center gap-1.5 rounded-md border border-[#006CE4] bg-white px-3 py-1.5 text-sm font-semibold text-[#006CE4] hover:bg-blue-50"><Layers className="h-4 w-4" /> {t("Edit massal")}</button>
          <button data-testid="cal-save-button" disabled={!count || saving} onClick={save} className="flex items-center gap-1.5 rounded-md bg-[#006CE4] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#0052B4] disabled:opacity-40"><Save className="h-4 w-4" /> {t("Simpan")} ({count})</button>
        </div>
      </div>
      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="border-collapse text-sm" data-testid="rates-grid">
            <thead>
              <tr className="bg-[#F8FAFC]">
                <th className="sticky left-0 z-10 min-w-[200px] border border-[#CBD5E1] bg-[#F8FAFC] px-3 py-2 text-left">Tipe kamar</th>
                {dates.map((d) => (
                  <th key={d} className={`border border-[#CBD5E1] px-1 py-2 text-xs font-semibold ${weekend[d] ? "bg-blue-50 text-[#006CE4]" : ""}`}>
                    <div className="uppercase">{fmtDate(d, "EEE")}</div><div className="text-base font-bold">{fmtDate(d, "d")}</div><div className="font-normal text-slate-500">{fmtDate(d, "MMM")}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid?.rows.map(({ room, days }) => [
                <tr key={room.id + "h"} className="bg-slate-100"><td colSpan={dates.length + 1} className="sticky left-0 border border-[#CBD5E1] px-3 py-2 font-ex-heading font-bold">{room.name} <span className="font-normal text-slate-500">· {room.total_rooms} kamar fisik</span></td></tr>,
                <tr key={room.id + "s"}>
                  <td className="sticky left-0 z-10 border border-[#CBD5E1] bg-white px-3 text-xs font-medium text-slate-600">Status kamar</td>
                  {days.map((d) => {
                    const closed = val(room.id, d, "closed");
                    return <Cell key={d.date} weekend={weekend[d.date]}>
                      <button data-testid={`cell-closed-${room.id}-${d.date}`} onClick={() => setVal(room.id, d.date, "closed", !closed)}
                        className={`h-8 w-full text-xs font-bold transition-colors ${closed ? "bg-[#DC2626] text-white" : "bg-green-100 text-green-800 hover:bg-green-200"} ${edits[k(room.id, d.date)]?.closed !== undefined ? "ring-2 ring-inset ring-amber-400" : ""}`}>
                        {closed ? "TUTUP" : "BUKA"}
                      </button></Cell>;
                  })}
                </tr>,
                <tr key={room.id + "a"}>
                  <td className="sticky left-0 z-10 border border-[#CBD5E1] bg-white px-3 text-xs font-medium text-slate-600">Kamar dijual</td>
                  {days.map((d) => <Cell key={d.date} weekend={weekend[d.date]}><NumIn testid={`cell-allot-${room.id}-${d.date}`} value={val(room.id, d, "allotment")} dirty={edits[k(room.id, d.date)]?.allotment !== undefined} onChange={(v) => setVal(room.id, d.date, "allotment", v)} /></Cell>)}
                </tr>,
                <tr key={room.id + "b"}>
                  <td className="sticky left-0 z-10 border border-[#CBD5E1] bg-white px-3 text-xs font-medium text-slate-600">Terjual / sisa</td>
                  {days.map((d) => <Cell key={d.date} weekend={weekend[d.date]} className="text-xs text-slate-600"><span className="font-semibold text-slate-900">{d.sold}</span> / <span className={d.available === 0 ? "font-bold text-[#DC2626]" : ""}>{d.available}</span></Cell>)}
                </tr>,
                <tr key={room.id + "p"}>
                  <td className="sticky left-0 z-10 border border-[#CBD5E1] bg-white px-3 text-xs font-medium text-slate-600">Harga (Rp)</td>
                  {days.map((d) => <Cell key={d.date} weekend={weekend[d.date]}><NumIn testid={`cell-price-${room.id}-${d.date}`} value={val(room.id, d, "price")} dirty={edits[k(room.id, d.date)]?.price !== undefined} onChange={(v) => setVal(room.id, d.date, "price", v)} /></Cell>)}
                </tr>,
              ])}
            </tbody>
          </table>
        </div>
      </Panel>
      <BulkEditDialog open={bulk} onOpenChange={setBulk} rooms={grid?.rows.map((r) => r.room) || []} propertyId={property.id} onSaved={load} />
    </div>
  );
}
